import { istDateKey } from "../lib/admin-time";
/* eslint-disable no-unused-vars */
import axios from "axios";
import React, { createContext, useContext, useRef, useMemo } from "react";
import qs from "qs";
import { AccountContext } from "../account/account";
import axiosRetry from "axios-retry";
import { isObject } from "lodash";

const APIContext = createContext();

const BASE_API_URL = process.env.REACT_APP_GRIGORA_API_URL;


axiosRetry(axios, {
  retryDelay: (retryCount) => {
    return retryCount * 1000; // retry after 1, 2, 3, ... seconds
  },
});

function APIProvider({ children }) {
  // Get User Details Function

  const { userAttributes, currentUser, idToken, accessToken } =
    useContext(AccountContext);

  const getUserDetails = async ({ refresh = false } = {}) => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/list";
      axios
        .get(BASE_API_URL + endpoint, {
          params: refresh ? { refresh: true } : undefined,
          ...(refresh ? { "axios-retry": { retries: 0 } } : {}),
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          const users = (res.data.users || []).map((userData) => {
            var userAttribute = { ...(userData.attributes_parsed || {}) };
            (userData.Attributes || []).forEach((attri) => {
              userAttribute[attri.Name] = attri.Value;
            });
            return {
              ...userData,
              attributes_parsed: userAttribute,
            };
          });
          resolve(users);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // User Addon Management Functions
  const getUserAddons = async (sub) => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/addons/list";
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ sub }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.addons ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getAvailableAddons = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/addons/available";
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({}), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.addons ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const assignAddonToUser = async (sub, addon_id) => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/addons/assign";
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ sub, addon_id }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const unassignAddonFromUser = async (sub, assignment_id) => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/addons/unassign";
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ sub, assignment_id }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const downloadUserList = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/user/list/download";
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
          responseType: 'blob',
        },)
        .then((res) => {
          // Save file

          const href = URL.createObjectURL(res.data);

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', 'users.csv'); //or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);

          resolve();

        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get User Attributes Function

  const getUserAttribute = async (sub) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/attributes?sub=${sub}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Update User Attributes Function

  const updateUserAttributes = async (username, family_name, given_name) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/update`;
      axios
        .post(BASE_API_URL + endpoint, {
          username: username,
          attribute: {
            "custom:family_name": family_name,
            "custom:given_name": given_name,
          },
        }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get User Email Verify Function

  const getUserEmailVerify = async (username) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/verify?username=${username}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Disable User Account Function

  const userAccountDisable = async (username) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/disable`;
      axios
        .post(BASE_API_URL + endpoint, { username: username }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Enable User Account Function

  const userAccountEnable = async (username) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/enable`;
      axios
        .post(BASE_API_URL + endpoint, { username: username }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // User Delete Function

  const userDelete = async (username) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/delete`;
      axios
        .post(BASE_API_URL + endpoint, { username: username }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Change User Password Function

  const resetUserPassword = async (email, password) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/reset/password`;
      axios
        .post(BASE_API_URL + endpoint, {
          email: email,
          password: password,
        }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Project Data Function

  const getProjectData = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/all`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Project Data by Project ID Function

  const getProjectDataById = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project?id=${projectID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Project Analytics by project ID function

  const getProjectAnalytics = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/analytics?id=${projectID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.analytics ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get All Pages for a Project function

  const getProjectAllPages = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/pages/all?id=${projectID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get A List of CMS Posts by ProjectID function

  const getListOfCMSPosts = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/cms/posts/all?id=${projectID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Items ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //Get All CMS Categories for a given ProjectID function

  const getAllCMSCategories = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/cms/categories/all?id=${projectID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Items ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Regenerate the task in the publishing queue function

  const getRegenerateTheTask = async (projectID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/project/regenerate?id=${projectID}`;
      axios
        .put(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // List of all Themes function

  const listOfAllThemes = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/list`;
      const formData = new FormData();
      axios
        .post(BASE_API_URL + endpoint, formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // List of all Webinars function

  const listOfAllWebinars = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/webinars/list`;
      axios
        .post(BASE_API_URL + endpoint, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Theme function

  const getTheme = async (themeID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/get`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ theme_id: themeID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Fill theme meta with AI (keywords, one-liner, use case, why choose) from screenshot and name
  const fillThemeWithAI = async (themeName, themeThumbnail, themeMeta) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/fill-with-ai`;
      axios
        .post(
          BASE_API_URL + endpoint,
          {
            theme_name: themeName,
            theme_thumbnail: themeThumbnail,
            theme_meta: themeMeta || {},
          },
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "application/json",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Webinar function

  const getWebinar = async (webinarID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/webinars/get`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ webinar_id: webinarID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Theme function

  const getSection = async (sectionID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/sections/get`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ section_id: sectionID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // ── AI website builder adapter control ──
  // These hit the admin API's /ai-builder-adapter proxy, which forwards to the
  // builder (where the CLIs run). JSON bodies, not form-encoded, to match.
  const getAiBuilderAdapterState = async () => {
    const res = await axios.get(BASE_API_URL + "/ai-builder-adapter/state", {
      headers: { Authorization: idToken },
    });
    return res.data;
  };

  const getCliAgentUsage = async () => {
    const res = await axios.get(BASE_API_URL + "/ai-builder-adapter/usage", {
      headers: { Authorization: idToken },
      timeout: 60000,
    });
    return res.data;
  };

  const getAiBuilderAdapterModels = async (adapter) => {
    const res = await axios.get(BASE_API_URL + "/ai-builder-adapter/models", {
      headers: { Authorization: idToken },
      params: { adapter },
    });
    return res.data;
  };

  const validateAiBuilderAdapter = async (adapter, model, effort = "") => {
    const res = await axios.post(
      BASE_API_URL + "/ai-builder-adapter/validate",
      { adapter, model, effort },
      { headers: { Authorization: idToken, "Content-Type": "application/json" } }
    );
    return res.data;
  };

  const saveAiBuilderAdapter = async (adapter, model, effort = "", force = false) => {
    // validateStatus lets us read the builder's 409 (unusable) body instead of
    // throwing, so the UI can show why a save was refused.
    const res = await axios.post(
      BASE_API_URL + "/ai-builder-adapter/save",
      { adapter, model, effort, force },
      {
        headers: { Authorization: idToken, "Content-Type": "application/json" },
        validateStatus: () => true,
      }
    );
    return { status: res.status, data: res.data };
  };

  const getSettingsPromoCode = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/admin/get_sale_promo_code`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({}), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const updateSettingsPromoCode = async (promo_code) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/admin/update_sale_promo_code`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ promo_code: promo_code }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const deleteSettingsPromoCode = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/admin/delete_sale_promo_code`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({}), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // To create a new theme function

  const createNewTheme = async (
    themeName,
    themeDescription,
    themeThumbnail,
    themeLongThumbnail,
    themeIsSinglePage,
    themeMeta,
    themeOwner,
    themeSlug,
    projectId
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/create`;
      const formData = new FormData();
      formData.append("theme_name", themeName);
      formData.append("theme_description", themeDescription);
      formData.append("theme_thumbnail", themeThumbnail);
      formData.append("theme_long_thumbnail", themeLongThumbnail);
      formData.append("theme_is_single_page", themeIsSinglePage ? "true" : "false");
      formData.append("theme_meta", isObject(themeMeta) ? JSON.stringify(themeMeta) : JSON.stringify({}));
      formData.append("theme_owner", themeOwner);
      formData.append("theme_slug", themeSlug);
      formData.append("project_id", projectId);

      axios
        .post(
          BASE_API_URL + endpoint,
          formData,
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // To create a new webinar function

  const createNewWebinar = async (
    webinarTitle,
    webinarDescription,
    webinarThumbnail,
    webinarYoutubeLink,
    webinarSlug,
    webinarScheduledTime,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/webinars/create`;
      const formData = new FormData();
      formData.append("webinar_title", webinarTitle);
      formData.append("webinar_description", webinarDescription);
      formData.append("webinar_thumbnail", webinarThumbnail);
      formData.append("webinar_youtube_link", webinarYoutubeLink || "");
      formData.append("webinar_slug", webinarSlug);
      if (webinarScheduledTime) {
        formData.append("webinar_scheduled_time", String(webinarScheduledTime));
      }

      axios
        .post(
          BASE_API_URL + endpoint,
          formData,
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // To create a new section function

  const createNewSection = async (
    sectionName,
    sectionDescription,
    sectionThumbnail,
    sectionCategory,
    sectionMeta,
    sectionJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/sections/create`;
      const formData = new FormData();
      formData.append("section_name", sectionName);
      formData.append("section_description", sectionDescription);
      formData.append("section_thumbnail", sectionThumbnail);
      formData.append("section_category", sectionCategory);
      formData.append("section_meta", isObject(sectionMeta) ? JSON.stringify(sectionMeta) : JSON.stringify({}));
      formData.append("section_json", sectionJSON);

      axios
        .post(
          BASE_API_URL + endpoint,
          formData,
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Delete Theme function

  const deleteTheme = async (themeID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/delete`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ theme_id: themeID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Delete Webinar function
  const deleteWebinar = async (webinarID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/webinars/delete`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ webinar_id: webinarID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Delete Section function
  const deleteSection = async (sectionID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/sections/delete`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ section_id: sectionID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Update Theme function

  const updateTheme = async (
    themeID,
    themeName,
    themeDescription,
    themeThumbnail,
    themeLongThumbnail,
    themeIsSinglePage,
    themeMeta,
    themeOwner,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/update`;
      var formData = new FormData();
      formData.append("theme_id", themeID);
      formData.append("theme_name", themeName);
      formData.append("theme_description", themeDescription);
      formData.append("theme_thumbnail", themeThumbnail);
      formData.append("theme_long_thumbnail", themeLongThumbnail);
      formData.append("theme_meta", isObject(themeMeta) ? JSON.stringify(themeMeta) : JSON.stringify({}));
      formData.append("theme_owner", themeOwner);
      formData.append("theme_is_single_page", themeIsSinglePage ? "true" : "false");

      axios
        .post(
          BASE_API_URL + endpoint,
          formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Update Webinar function

  const updateWebinar = async (
    webinarID,
    webinarTitle,
    webinarDescription,
    webinarThumbnail,
    webinarYoutubeLink,
    webinarSlug,
    webinarScheduledTime,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/webinars/update`;
      var formData = new FormData();
      formData.append("webinar_id", webinarID);
      formData.append("webinar_title", webinarTitle);
      formData.append("webinar_description", webinarDescription);
      formData.append("webinar_thumbnail", webinarThumbnail);
      formData.append("webinar_youtube_link", webinarYoutubeLink || "");
      formData.append("webinar_slug", webinarSlug);
      if (webinarScheduledTime) {
        formData.append("webinar_scheduled_time", String(webinarScheduledTime));
      }

      axios
        .post(
          BASE_API_URL + endpoint,
          formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Update Section function

  const updateSection = async (
    sectionID,
    sectionName,
    sectionDescription,
    sectionThumbnail,
    sectionCategory,
    sectionMeta,
    sectionJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/sections/update`;
      var formData = new FormData();
      formData.append("section_id", sectionID);
      formData.append("section_name", sectionName);
      formData.append("section_description", sectionDescription);
      formData.append("section_thumbnail", sectionThumbnail);
      formData.append("section_category", sectionCategory);
      formData.append("section_meta", isObject(sectionMeta) ? JSON.stringify(sectionMeta) : JSON.stringify({}));
      formData.append("section_json", sectionJSON);
      axios
        .post(
          BASE_API_URL + endpoint,
          formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Sync Theme function

  const syncTheme = async (themeID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/themes/sync`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ theme_id: themeID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Analytics Data for a specified date range function

  const getAnalyticsData = async (startDateString) => {
    const currentDate = istDateKey();
    return await new Promise((resolve, reject) => {
      const endpoint = `/analytics?startDate=${startDateString}&endDate=${currentDate}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.data ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // List of all Sections function

  const listOfAllSections = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/sections/list`;
      axios
        .post(BASE_API_URL + endpoint, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get AI Section function

  const getAISection = async (sectionID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/get`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ section_id: sectionID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // To create a new AI section function

  const createNewAISection = async (
    sectionName,
    sectionDescription,
    sectionThumbnail,
    sectionCategory,
    sectionMeta,
    sectionJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/create`;
      const formData = new FormData();
      formData.append("section_name", sectionName);
      formData.append("section_description", sectionDescription);
      if (sectionThumbnail) {
        formData.append("section_thumbnail", sectionThumbnail);
      }
      formData.append("section_category", sectionCategory);
      formData.append("section_meta", isObject(sectionMeta) ? JSON.stringify(sectionMeta) : JSON.stringify({}));
      formData.append("section_json", sectionJSON);

      axios
        .post(
          BASE_API_URL + endpoint,
          formData,
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Delete AI Section function
  const deleteAISection = async (sectionID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/delete`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ section_id: sectionID }), {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Update AI Section function

  const updateAISection = async (
    sectionID,
    sectionName,
    sectionDescription,
    sectionThumbnail,
    sectionCategory,
    sectionMeta,
    sectionJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/update`;
      var formData = new FormData();
      formData.append("section_id", sectionID);
      formData.append("section_name", sectionName);
      formData.append("section_description", sectionDescription);
      if (sectionThumbnail) {
        formData.append("section_thumbnail", sectionThumbnail);
      }
      formData.append("section_category", sectionCategory);
      formData.append("section_meta", isObject(sectionMeta) ? JSON.stringify(sectionMeta) : JSON.stringify({}));
      formData.append("section_json", sectionJSON);
      axios
        .post(
          BASE_API_URL + endpoint,
          formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // List of all AI Sections function

  const listOfAllAISections = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/list`;
      axios
        .post(BASE_API_URL + endpoint, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Sync AI Sections function
  const syncAISections = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-sections/sync`;
      axios
        .post(BASE_API_URL + endpoint, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Get Template function

  const getTemplate = async (templateID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/templates/get`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ template_id: templateID }), {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // To create a new template function

  const createNewTemplate = async (
    templateName,
    templateDescription,
    templateThumbnail,
    templateCategory,
    templateMeta,
    templateJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/templates/create`;
      const formData = new FormData();
      formData.append("template_name", templateName);
      formData.append("template_description", templateDescription);
      formData.append("template_thumbnail", templateThumbnail);
      formData.append("template_category", templateCategory);
      formData.append("template_meta", isObject(templateMeta) ? JSON.stringify(templateMeta) : JSON.stringify({}));
      formData.append("template_json", templateJSON);

      axios
        .post(
          BASE_API_URL + endpoint,
          formData,
          {
            headers: {
              Authorization: idToken,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Delete Template function

  const deleteTemplate = async (templateID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/templates/delete`;
      axios
        .post(BASE_API_URL + endpoint, qs.stringify({ template_id: templateID }), {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  //  Update Template function

  const updateTemplate = async (
    templateID,
    templateName,
    templateDescription,
    templateThumbnail,
    templateCategory,
    templateMeta,
    templateJSON,
  ) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/templates/update`;
      var formData = new FormData();
      formData.append("template_id", templateID);
      formData.append("template_name", templateName);
      formData.append("template_description", templateDescription);
      formData.append("template_thumbnail", templateThumbnail);
      formData.append("template_category", templateCategory);
      formData.append("template_meta", isObject(templateMeta) ? JSON.stringify(templateMeta) : JSON.stringify({}));
      formData.append("template_json", templateJSON);
      axios
        .post(
          BASE_API_URL + endpoint,
          formData, {
          headers: {
            Authorization: idToken,
            "Content-Type": "multipart/form-data",
          },
        },
        )
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // List of all Templates function

  const listOfAllTemplates = async () => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/templates/list`;
      axios
        .post(BASE_API_URL + endpoint, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Banner Functions

  const listOfAllBanners = async () => {
    return await new Promise((resolve, reject) => {
      adminActions("banners/list", {})
        .then((res) => {
          resolve(res?.Output ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getBanner = async (bannerID) => {
    return await new Promise((resolve, reject) => {
      adminActions("banners/get", { banner_id: bannerID })
        .then((res) => {
          resolve(res?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const createNewBanner = async (bannerData) => {
    return await new Promise((resolve, reject) => {
      const params = {
        banner_name: bannerData.banner_name || "",
        banner_text: bannerData.banner_text,
        banner_color: bannerData.banner_color,
        text_color: bannerData.text_color || "",
        text_alignment: bannerData.text_alignment || "left",
        text_margin: bannerData.text_margin ? JSON.stringify(bannerData.text_margin) : null,
        button_url: bannerData.button_url || "",
        button_text: bannerData.button_text || "",
        button_color: bannerData.button_color || "",
        button_text_color: bannerData.button_text_color || "",
        button_margin: bannerData.button_margin ? JSON.stringify(bannerData.button_margin) : null,
        button_padding: bannerData.button_padding ? JSON.stringify(bannerData.button_padding) : null,
        is_dismissable: bannerData.is_dismissable ? "true" : "false",
        dismiss_days: bannerData.dismiss_days || 7,
        dismiss_icon_color: bannerData.dismiss_icon_color || "",
        start_date: bannerData.start_date || "",
        expiry_date: bannerData.expiry_date || "",
        published: bannerData.published ? "true" : "false",
        layout: bannerData.layout || "text-left-button-right",
        font_family: bannerData.font_family || "Inter",
        text_font_size: bannerData.text_font_size || 14,
        button_font_size: bannerData.button_font_size || 14,
        gap: bannerData.gap || 16,
        button_layout: bannerData.button_layout || "filled",
        secondary_button_layout: bannerData.secondary_button_layout || "filled",
        secondary_button_enabled: bannerData.secondary_button_enabled ? "true" : "false",
        secondary_button_url: bannerData.secondary_button_url || "",
        secondary_button_text: bannerData.secondary_button_text || "",
        secondary_button_color: bannerData.secondary_button_color || "",
        secondary_button_text_color: bannerData.secondary_button_text_color || "",
        secondary_button_margin: bannerData.secondary_button_margin ? JSON.stringify(bannerData.secondary_button_margin) : null,
        secondary_button_padding: bannerData.secondary_button_padding ? JSON.stringify(bannerData.secondary_button_padding) : null,
        conditions: bannerData.conditions ? JSON.stringify(bannerData.conditions) : null,
      };
      adminActions("banners/create", params)
        .then((res) => {
          resolve(res?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const updateBanner = async (bannerID, bannerData) => {
    return await new Promise((resolve, reject) => {
      const params = {
        banner_id: bannerID,
        banner_name: bannerData.banner_name || "",
        banner_text: bannerData.banner_text,
        banner_color: bannerData.banner_color,
        text_color: bannerData.text_color || "",
        text_alignment: bannerData.text_alignment || "left",
        text_margin: bannerData.text_margin ? JSON.stringify(bannerData.text_margin) : null,
        button_url: bannerData.button_url || "",
        button_text: bannerData.button_text || "",
        button_color: bannerData.button_color || "",
        button_text_color: bannerData.button_text_color || "",
        button_margin: bannerData.button_margin ? JSON.stringify(bannerData.button_margin) : null,
        button_padding: bannerData.button_padding ? JSON.stringify(bannerData.button_padding) : null,
        is_dismissable: bannerData.is_dismissable ? "true" : "false",
        dismiss_days: bannerData.dismiss_days || 7,
        dismiss_icon_color: bannerData.dismiss_icon_color || "",
        start_date: bannerData.start_date || "",
        expiry_date: bannerData.expiry_date || "",
        published: bannerData.published ? "true" : "false",
        layout: bannerData.layout || "text-left-button-right",
        font_family: bannerData.font_family || "Inter",
        text_font_size: bannerData.text_font_size || 14,
        button_font_size: bannerData.button_font_size || 14,
        gap: bannerData.gap || 16,
        button_layout: bannerData.button_layout || "filled",
        secondary_button_layout: bannerData.secondary_button_layout || "filled",
        secondary_button_enabled: bannerData.secondary_button_enabled ? "true" : "false",
        secondary_button_url: bannerData.secondary_button_url || "",
        secondary_button_text: bannerData.secondary_button_text || "",
        secondary_button_color: bannerData.secondary_button_color || "",
        secondary_button_text_color: bannerData.secondary_button_text_color || "",
        secondary_button_margin: bannerData.secondary_button_margin ? JSON.stringify(bannerData.secondary_button_margin) : null,
        secondary_button_padding: bannerData.secondary_button_padding ? JSON.stringify(bannerData.secondary_button_padding) : null,
        conditions: bannerData.conditions ? JSON.stringify(bannerData.conditions) : null,
      };
      adminActions("banners/update", params)
        .then((res) => {
          resolve(res?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const deleteBanner = async (bannerID) => {
    return await new Promise((resolve, reject) => {
      adminActions("banners/delete", { banner_id: bannerID })
        .then((res) => {
          resolve(res?.Output ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  async function adminActions(action, params, type) {
    return new Promise(async (resolve, reject) => {

      try {
        if (type === "formdata") {
          var form = new FormData();
          for (var key in params) {
            if (params[key]) {
              form.append(key, params[key]);
            }
          }

          let response = await axios.post(
            `${BASE_API_URL}/${action}`,
            form,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: idToken,
              }
            }
          );

          resolve(response.data);
        } else {
          let response = await axios.post(
            `${BASE_API_URL}/${action}`,
            qs.stringify(params),
            {
              headers: {
                Authorization: idToken,
              },
            }
          );
          resolve(response.data);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // Toggle AppSumo Banner Function
  const toggleAppSumoBanner = async (sub, showBanner) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/appsumo/toggle_banner`;
      axios
        .post(BASE_API_URL + endpoint, {
          sub: sub,
          show_banner: showBanner
        }, {
          headers: {
            Authorization: idToken,
          },
        },)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Toggle Beta Program Access
  const toggleBetaProgram = async (sub, program, enabled) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/user/beta/toggle`;
      axios
        .post(
          BASE_API_URL + endpoint,
          {
            sub: sub,
            program: program,
            enabled: enabled,
          },
          {
            headers: {
              Authorization: idToken,
            },
          },
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  // Apps Management Functions
  const getAppsList = async (status) => {
    return await new Promise((resolve, reject) => {
      let endpoint = "/apps";
      if (status) endpoint += `?status=${status}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.apps ?? []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getAppDetails = async (appID) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/apps/${appID}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data?.app ?? {});
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const reviewApp = async (appID, status, notes) => {
    return await new Promise((resolve, reject) => {
      const endpoint = "/apps/review";
      axios
        .post(BASE_API_URL + endpoint, { app_id: appID, status, notes }, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const unapproveApp = async (appID) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(BASE_API_URL + `/apps/${appID}/unapprove`, {}, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  const updateAppFeatured = async (appID, payload) => {
    return await new Promise((resolve, reject) => {
      axios
        .patch(BASE_API_URL + `/apps/${appID}`, payload, {
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  const deleteApp = async (appID) => {
    return await new Promise((resolve, reject) => {
      axios
        .delete(BASE_API_URL + `/apps/${appID}`, {
          headers: { Authorization: idToken },
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  // App reviews (user-submitted reviews pending approval)
  const getPendingAppReviews = async () => {
    return await new Promise((resolve, reject) => {
      axios
        .get(BASE_API_URL + "/apps/reviews", {
          headers: { Authorization: idToken },
        })
        .then((res) => {
          resolve(res.data?.byApp ?? []);
        })
        .catch((err) => reject(err));
    });
  };

  const approveRejectAppReview = async (reviewId, status) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(BASE_API_URL + "/apps/reviews", { review_id: reviewId, status }, {
          headers: { Authorization: idToken, "Content-Type": "application/json" },
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  const getAiUsageAnalytics = async (days = 30) => {
    return await new Promise((resolve, reject) => {
      const endpoint = `/ai-usage?days=${encodeURIComponent(days)}`;
      axios
        .get(BASE_API_URL + endpoint, {
          headers: {
            Authorization: idToken,
          },
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getUserAiUsage = async (userID, days = 30) => {
    const res = await axios.get(`${BASE_API_URL}/ai-usage/users/${encodeURIComponent(userID)}`, {
      params: { days }, headers: { Authorization: idToken },
    });
    return res.data;
  };

  const uploadAiThemes = async (file, requestId) => {
    const body = new FormData();
    body.append("csv", file);
    body.append("request_id", requestId);
    const res = await axios.post(`${BASE_API_URL}/ai-themes/imports`, body, { headers: { Authorization: idToken } });
    return res.data;
  };
  const getAiThemes = async (search = "", offset = 0) => (await axios.get(`${BASE_API_URL}/ai-themes`, { params: { search, offset }, headers: { Authorization: idToken } })).data;
  const getAiThemeImports = async () => (await axios.get(`${BASE_API_URL}/ai-themes/imports`, { headers: { Authorization: idToken } })).data;
  const getAiThemeImport = async (id, offset = 0) => (await axios.get(`${BASE_API_URL}/ai-themes/imports/${id}`, { params: { offset }, headers: { Authorization: idToken } })).data;
  const getAiThemeHistory = async id => (await axios.get(`${BASE_API_URL}/ai-themes/${id}/history`, { headers: { Authorization: idToken } })).data;
  const aiThemeThumbnailUrl = theme => theme.thumbnail_url || `${BASE_API_URL}/ai-theme-thumbnails/${theme.id}/${theme.thumbnail_sha256}.jpg`;

  const allApiFunctions = {
    uploadAiThemes, getAiThemes, getAiThemeImports, getAiThemeImport, getAiThemeHistory, aiThemeThumbnailUrl,
    getUserAiUsage,
    getAnalyticsData,
    getAiUsageAnalytics,
    getUserDetails,
    getUserAttribute,
    updateUserAttributes,
    getUserEmailVerify,
    userAccountDisable,
    userAccountEnable,
    userDelete,
    resetUserPassword,
    getProjectData,
    getProjectDataById,
    getListOfCMSPosts,
    getAllCMSCategories,
    getRegenerateTheTask,
    listOfAllThemes,
    listOfAllWebinars,
    getTheme,
    fillThemeWithAI,
    getWebinar,
    createNewTheme,
    createNewWebinar,
    deleteTheme,
    deleteWebinar,
    updateTheme,
    updateWebinar,
    syncTheme,
    getProjectAnalytics,
    getProjectAllPages,
    downloadUserList,
    getAiBuilderAdapterState,
    getCliAgentUsage,
    getAiBuilderAdapterModels,
    validateAiBuilderAdapter,
    saveAiBuilderAdapter,
    getSettingsPromoCode,
    updateSettingsPromoCode,
    deleteSettingsPromoCode,
    listOfAllSections,
    createNewSection,
    deleteSection,
    getSection,
    updateSection,
    listOfAllAISections,
    syncAISections,
    createNewAISection,
    deleteAISection,
    getAISection,
    updateAISection,
    listOfAllTemplates,
    createNewTemplate,
    deleteTemplate,
    getTemplate,
    updateTemplate,
    listOfAllBanners,
    createNewBanner,
    deleteBanner,
    getBanner,
    updateBanner,
    adminActions,
    getUserAddons,
    getAvailableAddons,
    assignAddonToUser,
    unassignAddonFromUser,
    toggleAppSumoBanner,
    toggleBetaProgram,
    getAppsList,
    getAppDetails,
    reviewApp,
    unapproveApp,
    updateAppFeatured,
    deleteApp,
    getPendingAppReviews,
    approveRejectAppReview,
  };

  const latestFunctionsRef = useRef(allApiFunctions);
  latestFunctionsRef.current = allApiFunctions;

  // Stable memoized functions proxy: each function keeps the exact same reference forever.
  // When executed, it always calls the latest implementation with the latest token.
  // This completely prevents useEffect dependencies from re-running on every token refresh!
  const stableApi = useMemo(() => {
    const stable = {};
    Object.keys(allApiFunctions).forEach((fnName) => {
      stable[fnName] = (...args) => {
        const fn = latestFunctionsRef.current[fnName];
        if (typeof fn === "function") {
          return fn(...args);
        }
        return Promise.reject(new Error(`API function ${fnName} not found`));
      };
    });
    return stable;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <APIContext.Provider value={stableApi}>
      {children}
    </APIContext.Provider>
  );
}

export { APIProvider, APIContext };
